import React, { useCallback, useState } from 'react';
import { Box, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { useNotificationCounts } from '../../hooks/useNotificationCounts';
import { usePermissions } from '../../hooks/usePermissions';
import { QuickAnswerDialog, useQuickAnswerShortcut } from '../../pages/answers/components/QuickAnswerDialog';
import { NewTaskAnywhere, useNewTaskShortcut } from '../../pages/tasks/NewTaskAnywhere';
import { TaskAlerts } from '../../pages/tasks/TaskAlerts';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const { isInvestor } = usePermissions();
  // Fetched once here and passed down — Header's bell and Sidebar's nav
  // badges show the same counts, so they share a single poll instead of
  // each hitting these endpoints independently.
  const notificationCounts = useNotificationCounts(60000, !isInvestor);
  // Answer Desk quick answers (Ctrl/⌘+K or the header button). Mounted on
  // first open so pages that never use it don't load the answers.
  const [quickAnswersOpen, setQuickAnswersOpen] = useState(false);
  const [quickAnswersMounted, setQuickAnswersMounted] = useState(false);
  const openQuickAnswers = useCallback(() => {
    setQuickAnswersMounted(true);
    setQuickAnswersOpen(true);
  }, []);
  useQuickAnswerShortcut(!isInvestor, openQuickAnswers);
  // New task (header button or T) — mounted on first open, like quick answers.
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [newTaskMounted, setNewTaskMounted] = useState(false);
  const openNewTask = useCallback(() => {
    setNewTaskMounted(true);
    setNewTaskOpen(true);
  }, []);
  useNewTaskShortcut(!isInvestor, openNewTask);

  const handleMenuClick = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Header
        open={sidebarOpen}
        onMenuClick={handleMenuClick}
        notificationCounts={notificationCounts}
        onQuickAnswers={isInvestor ? undefined : openQuickAnswers}
        onNewTask={isInvestor ? undefined : openNewTask}
      />
      <Sidebar open={sidebarOpen} onClose={handleSidebarClose} isMobile={isMobile} notificationCounts={notificationCounts} />
      <Box
        component="main"
        sx={{
          // Takes whatever the sidebar leaves; minWidth 0 stops a wide table
          // inside from stretching the page past the screen.
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 2, sm: 3 },
          minHeight: '100vh',
          transition: 'none',
        }}
      >
        <Toolbar />
        {children}
      </Box>
      {quickAnswersMounted && !isInvestor && (
        <QuickAnswerDialog open={quickAnswersOpen} onClose={() => setQuickAnswersOpen(false)} />
      )}
      {newTaskMounted && !isInvestor && (
        <NewTaskAnywhere
          open={newTaskOpen}
          onClose={() => setNewTaskOpen(false)}
          onCreated={() => notificationCounts.refreshTaskSummary()}
        />
      )}
      {!isInvestor && <TaskAlerts summary={notificationCounts.taskSummary} onRead={notificationCounts.refreshTaskSummary} />}
    </Box>
  );
};
